import {
  createSelector,
  createSlice,
  current,
  PayloadAction,
  Selector
} from '@reduxjs/toolkit';
import {
  differenceBy,
  differenceWith,
  groupBy,
  isEqual,
  unionBy
} from 'lodash';
import { CheckInfoType, CheckWritingResponse } from 'adapters/types';
import { REDUX_SLICE_KEYS, VENDOR_NAME } from 'common/constants';
import { AuthorizePaymentInvoice } from 'pages/AuthorizePaymentPage/types';
import { getHarnessFlagValue } from 'pages/LandingPage/slice';
import { RootState } from 'store';
import { formatCurrency } from 'utils/formatCurrency';
import { STATUS, ERROR_KEYS } from '../dialogs/constants';
import { VENDORS_TABLE_FIELDS } from '../VendorsTable/constants';
import { getNumberFromCurrency } from '../VendorsTable/helpers';
import {
  DeleteApprovalRequestPayload,
  OpenGlCalendarsType,
  PrintChecksCIBDropdownType,
  PrintChecksRequestPayload,
  PrintChecksResponse,
  PrintChecksState,
  PrintChecksTableData,
  SelectPrintedOkCheckPayload
} from './types';
import { getPartiallyPrintedChecks } from './utils';
import type { CashInBankAccounts } from 'pages/AuthorizePaymentPage/interCompany/types';

export const initialState: PrintChecksState = {
  lockStatusData: {},
  selectedVendors: [],
  postingDescription: '',
  printOrder: VENDOR_NAME,
  isCancelBtnDisabled: false,
  isPrintBtnLoading: false,
  isPrintBtnDisabledOnRowFiltering: false,
  isPrintBtnDisabled: false,
  cutOffDisablePrintBtn: false,
  selectedCIBAccount: {
    accountNumber: '',
    accountCompany: '',
    accountName: '',
    postingDescriptionRequired: false,
    printerName: '',
    index: -1
  },
  selectedInterCompanyCIBAccount: {
    companyId: '',
    accountName: '',
    accountNumber: ''
  },
  openGlCalendar: {
    currentStart: '',
    postaheadStart: '',
    glCutoffDate: '',
    glCutoffTime: ''
  },
  isPrintChecksDialogOpen: false,
  isPrintSuccessDialogOpen: false,
  isPrintFailureDialogOpen: false,
  printChecksData: [],
  printChecksResponse: {
    totalNoOfVendors: 0,
    totalOwed: '',
    vendors: []
  },
  printResponse: {
    printOkList: [],
    printFailureList: []
  },
  printedOKChecks: [],
  isLoadingPrintChecks: false,
  isWorkflowPending: false,
  checkNoInputError: false,
  manualCheckNoDetails: {
    dmsPrinterNo: undefined,
    printerId: undefined,
    archiveOnly: undefined,
    manualEntryCheckSequence: {
      checkNumber: null,
      useSequenceForFutureChecks: null
    }
  }
};

export const printChecksSlice = createSlice({
  name: REDUX_SLICE_KEYS.printChecks,
  initialState,
  reducers: {
    setSelectedVendors: (
      state,
      action: PayloadAction<PrintChecksTableData[]>
    ): void => {
      state.selectedVendors = action.payload;
    },
    setIsLoadingPrintChecks: (state, action: PayloadAction<boolean>): void => {
      state.isLoadingPrintChecks = action.payload;
    },
    setPrintChecksData: (
      state,
      action: PayloadAction<PrintChecksTableData[]>
    ): void => {
      state.printChecksData = action.payload;
    },
    setPrintChecksResponse: (
      state,
      action: PayloadAction<PrintChecksResponse>
    ): void => {
      state.printChecksResponse = action.payload;
    },
    setIsPrintChecksDialogOpen: (
      state,
      action: PayloadAction<boolean>
    ): void => {
      state.isPrintChecksDialogOpen = action.payload;
    },
    setCheckNoInputError: (state, action: PayloadAction<boolean>): void => {
      state.checkNoInputError = action.payload;
    },

    setIsPrintSuccessDialogOpen: (
      state,
      action: PayloadAction<boolean>
    ): void => {
      state.isPrintSuccessDialogOpen = action.payload;
    },
    setIsPrintFailureDialogOpen: (
      state,
      action: PayloadAction<boolean>
    ): void => {
      state.isPrintFailureDialogOpen = action.payload;
    },
    setCancelBtnDisabled: (state, action: PayloadAction<boolean>): void => {
      state.isCancelBtnDisabled = action.payload;
    },
    setPrintBtnLoading: (state, action: PayloadAction<boolean>): void => {
      state.isPrintBtnLoading = action.payload;
    },
    setPrintBtnDisabled: (state, action: PayloadAction<boolean>): void => {
      state.isPrintBtnDisabled = action.payload;
    },
    setCutOffDisablePrintBtn: (state, action: PayloadAction<boolean>): void => {
      state.cutOffDisablePrintBtn = action.payload;
    },
    setSelectedCIBAccount: (
      state,
      action: PayloadAction<PrintChecksCIBDropdownType>
    ): void => {
      state.selectedCIBAccount = action.payload;
    },
    setInterCompanyCIBAccount: (
      state,
      action: PayloadAction<CashInBankAccounts>
    ): void => {
      state.selectedInterCompanyCIBAccount = action.payload;
    },
    setOpenGlCalendar: (
      state,
      action: PayloadAction<OpenGlCalendarsType>
    ): void => {
      state.openGlCalendar = action.payload;
    },
    setCheckWritingResponse: (
      state,
      action: PayloadAction<CheckWritingResponse>
    ): void => {
      state.printResponse = action.payload;
    },
    clearCheckWritingResponse: (state): void => {
      state.printResponse = initialState.printResponse;
      state.isWorkflowPending = !state.isWorkflowPending;
    },
    selectPrintedOkChecks: (
      state,
      action: PayloadAction<SelectPrintedOkCheckPayload>
    ): void => {
      if (action.payload.selected) {
        state.printedOKChecks = unionBy(
          current(state).printedOKChecks,
          action.payload.checks,
          'checkNumber'
        );
      } else {
        state.printedOKChecks = differenceBy(
          current(state).printedOKChecks,
          action.payload.checks,
          'checkNumber'
        );
      }
    },
    removeFromPrintOKList: (state, action: PayloadAction<string>): void => {
      state.printResponse.printOkList = state.printResponse.printOkList.filter(
        vendor => vendor.vendorNo !== action.payload
      );
    },
    removeFromSelectedVendors: (
      state,
      action: PayloadAction<string[]>
    ): void => {
      state.selectedVendors = state.selectedVendors.filter(
        item => !action.payload.includes(item.vendorNo)
      );
    },
    clearPostSelectedChecks: (state): void => {
      const lookup = new Set(state.printedOKChecks.map(item => item.vendorNo));
      state.printResponse.printOkList = state.printResponse.printOkList.filter(
        item => !lookup.has(item.vendorNo)
      );
      state.printedOKChecks = initialState.printedOKChecks;
      if (
        state.printResponse.printOkList.length &&
        !state.printResponse.printFailureList.length
      )
        state.isWorkflowPending = !state.isWorkflowPending;
    },
    clearSelectedVendors: (state): void => {
      state.selectedVendors = initialState.selectedVendors;
    },
    setPostingDescription: (state, action: PayloadAction<string>): void => {
      state.postingDescription = action.payload;
    },
    clearPostingDescription: (state): void => {
      state.postingDescription = initialState.postingDescription;
    },
    setPrintOrder: (state, action: PayloadAction<string>): void => {
      state.printOrder = action.payload;
    },
    setPrintBtnDisabledOnRowFiltering: (
      state,
      action: PayloadAction<boolean>
    ): void => {
      state.isPrintBtnDisabledOnRowFiltering = action.payload;
    },
    setDmsPrinterNo: (
      state,
      action: PayloadAction<string | undefined>
    ): void => {
      state.manualCheckNoDetails.dmsPrinterNo = action.payload;
    },
    setPrinterId: (state, action: PayloadAction<string | undefined>): void => {
      state.manualCheckNoDetails.printerId = action.payload;
    },
    setArchiveOnly: (state, action: PayloadAction<boolean>): void => {
      state.manualCheckNoDetails.archiveOnly = action.payload;
    },
    setManualCheckNumber: (
      state,
      action: PayloadAction<string | null>
    ): void => {
      state.manualCheckNoDetails.manualEntryCheckSequence.checkNumber =
        action.payload;
    },
    setIsSequenceForFutureChecks: (
      state,
      action: PayloadAction<boolean | null>
    ): void => {
      state.manualCheckNoDetails.manualEntryCheckSequence.useSequenceForFutureChecks =
        action.payload;
    }
  }
});

export const {
  setSelectedVendors,
  setCancelBtnDisabled,
  setPrintBtnLoading,
  setPrintBtnDisabledOnRowFiltering,
  setDmsPrinterNo,
  setPrinterId,
  setArchiveOnly,
  setManualCheckNumber,
  setIsSequenceForFutureChecks,
  setPrintBtnDisabled,
  setCutOffDisablePrintBtn,
  setSelectedCIBAccount,
  setInterCompanyCIBAccount,
  setIsPrintChecksDialogOpen,
  setIsPrintSuccessDialogOpen,
  setIsPrintFailureDialogOpen,
  setOpenGlCalendar,
  setCheckWritingResponse,
  clearCheckWritingResponse,
  selectPrintedOkChecks,
  removeFromPrintOKList,
  clearPostSelectedChecks,
  setPostingDescription,
  setPrintOrder,
  clearPostingDescription,
  clearSelectedVendors,
  removeFromSelectedVendors,
  setIsLoadingPrintChecks,
  setPrintChecksData,
  setPrintChecksResponse,
  setCheckNoInputError
} = printChecksSlice.actions;

export const getRootState = (state: RootState): RootState => state;

export const getInitialState = (state: RootState): PrintChecksState =>
  state.printChecks;

export const getSelectedVendors = createSelector(
  getInitialState,
  ({ selectedVendors }) => selectedVendors
);

export const getIsLoadingPrintChecks = createSelector(
  getInitialState,
  state => state.isLoadingPrintChecks
);

export const getPrintChecksData = createSelector(
  getInitialState,
  state => state.printChecksData
);

export const getPrintChecksResponse = createSelector(
  getInitialState,
  state => state.printChecksResponse
);

export const getGroupedSelectedVendors = createSelector(
  getSelectedVendors,
  selectedInvoices => groupBy(selectedInvoices, VENDORS_TABLE_FIELDS.vendorNo)
);

export const getTotalAmount = createSelector(
  getSelectedVendors,
  selectedVendors =>
    selectedVendors.reduce(
      (acc, check) => acc + getNumberFromCurrency(check.totalNetAmount),
      0
    )
);

export const getCheckNoInputError = createSelector(
  getInitialState,
  ({ checkNoInputError }) => checkNoInputError
);

export const getIsPrintChecksDialogOpen = createSelector(
  getInitialState,
  ({ isPrintChecksDialogOpen }) => isPrintChecksDialogOpen
);
export const getIsPrintSuccessDialogOpen = createSelector(
  getInitialState,
  ({ isPrintSuccessDialogOpen }) => isPrintSuccessDialogOpen
);
export const getIsPrintFailureDialogOpen = createSelector(
  getInitialState,
  ({ isPrintFailureDialogOpen }) => isPrintFailureDialogOpen
);

export const getIsCancelButtonDisabled = createSelector(
  getInitialState,
  ({ isCancelBtnDisabled }) => isCancelBtnDisabled
);

export const getIsPrintBtnLoading = createSelector(
  getInitialState,
  ({ isPrintBtnLoading }) => isPrintBtnLoading
);

export const getIsPrintBtnDisabled = createSelector(
  getInitialState,
  ({ isPrintBtnDisabled }) => isPrintBtnDisabled
);

export const getCutOffDisablePrintBtn = createSelector(
  getInitialState,
  ({ cutOffDisablePrintBtn }) => cutOffDisablePrintBtn
);

export const getSelectedCIBAccount = createSelector(
  getInitialState,
  ({ selectedCIBAccount }) => selectedCIBAccount
);

export const getInterCompanyCIBAccount = createSelector(
  getInitialState,
  ({ selectedInterCompanyCIBAccount }) => selectedInterCompanyCIBAccount
);

export const getPrinterName = createSelector(
  getSelectedCIBAccount,
  ({ printerName }) => printerName
);

export const getPaymentDate = createSelector(
  getRootState,
  ({ paymentDate }) => paymentDate.printChecks.paymentDate
);

export const getOpenGlCalendar = createSelector(
  getInitialState,
  ({ openGlCalendar }) => openGlCalendar
);

export const getCheckWritingResponseSuccess = createSelector(
  getInitialState,
  ({ printResponse: { printOkList } }) => printOkList
);

export const getPrintedChecksFormatted = createSelector(
  getCheckWritingResponseSuccess,
  printOkList =>
    printOkList.map(({ checkAmount, ...checkProps }: CheckInfoType) => ({
      checkAmount: formatCurrency(
        getNumberFromCurrency(checkAmount),
        'decimal'
      ),
      ...checkProps
    }))
);

export const getCheckWritingResponseFailure = createSelector(
  getInitialState,
  ({ printResponse: { printFailureList } }) => printFailureList
);

export const getChecksFailedFormatted = createSelector(
  getCheckWritingResponseFailure,
  printFailureList =>
    printFailureList.map(({ checkAmount, ...checkProps }: CheckInfoType) => ({
      checkAmount: formatCurrency(
        getNumberFromCurrency(checkAmount),
        'decimal'
      ),
      ...checkProps
    }))
);

export const getFailedChecksReason = createSelector(
  getInitialState,
  ({ printResponse: { printFailureList } }) => {
    const { errorText } =
      printFailureList.find(({ errorCode }) => errorCode === 'E403') || {};
    return errorText;
  }
);

export const getPrintedChecksWithoutErrors = createSelector(
  getCheckWritingResponseSuccess,
  printOkList =>
    printOkList.filter(
      ({ status, errorCode }) => status === STATUS.PRINTED && errorCode === ''
    )
);

export const getPrintedCheckWithArchiveError = createSelector(
  getCheckWritingResponseSuccess,
  printOkList =>
    printOkList.filter(({ status, errorText }) =>
      getPartiallyPrintedChecks(status, errorText, ERROR_KEYS.ARCHIVE)
    )
);

export const getPrintedCheckWithGLError = createSelector(
  getCheckWritingResponseSuccess,
  printOkList =>
    printOkList.filter(({ status, errorText }) =>
      getPartiallyPrintedChecks(status, errorText, ERROR_KEYS.GENERAL_LEDGER)
    )
);

export const getPrintedCheckWithCheckFormError = createSelector(
  getCheckWritingResponseSuccess,
  printOkList =>
    printOkList.filter(({ status, errorText }) =>
      getPartiallyPrintedChecks(status, errorText, ERROR_KEYS.CHECK_FORM)
    )
);

export const getPrintedOKChecks = createSelector(
  getInitialState,
  ({ printedOKChecks }) => printedOKChecks
);

export const getIsPaymentChecked = (
  check: CheckInfoType
): Selector<RootState, boolean> =>
  createSelector(
    getPrintedOKChecks,
    printedOkChecks =>
      !!printedOkChecks.find(
        ({ checkNumber }) => checkNumber === check.checkNumber
      )
  );

export const getToVoidChecks = createSelector(
  [getCheckWritingResponseSuccess, getPrintedOKChecks],
  (successfulChecks, printedOkChecks) =>
    differenceWith(successfulChecks, printedOkChecks, isEqual)
);

export const getPostingDescription = createSelector(
  getInitialState,
  ({ postingDescription }) => postingDescription
);

export const getPrintOrder = createSelector(
  getInitialState,
  ({ printOrder }) => printOrder
);

export const getPrintBtnDisabledOnRowFiltering = createSelector(
  getInitialState,
  ({ isPrintBtnDisabledOnRowFiltering }) => isPrintBtnDisabledOnRowFiltering
);

export const getUnlockSuccessfulInvoicesPayload = createSelector(
  [getPrintedOKChecks, getGroupedSelectedVendors],
  (printedOKChecks, groupedSelectedVendors): AuthorizePaymentInvoice[] =>
    printedOKChecks.flatMap(
      ({ vendorNo }) =>
        groupedSelectedVendors[vendorNo]?.[0]?.invoices.map(
          ({ invoiceId }) => ({
            vendorNo,
            invoiceId
          })
        ) || []
    )
);

export const getPrintChecksPayload = createSelector(
  [
    getPaymentDate,
    getPostingDescription,
    getPrintOrder,
    getSelectedCIBAccount,
    getInterCompanyCIBAccount,
    getSelectedVendors,
    getHarnessFlagValue('dmsAccountingIntercompanyPayment')
  ],
  (
    paymentDate,
    postingDescription,
    printOrder,
    selectedCIBAccount,
    selectedInterCompanyCIBAccount,
    selectedVendors,
    interCompanyFlag
  ): PrintChecksRequestPayload => ({
    paymentDate,
    postingDescription,
    printOrder,
    cashInBankAccount: interCompanyFlag
      ? selectedInterCompanyCIBAccount.accountNumber
      : selectedCIBAccount.accountNumber,
    vendors: selectedVendors
  })
);

export const getSelectedVendorInvoicePairs = createSelector(
  getSelectedVendors,
  (selectedVendors): DeleteApprovalRequestPayload =>
    selectedVendors.flatMap(({ vendorNo, invoices }) =>
      invoices.map(({ invoiceId }) => ({
        vendorNo,
        invoiceId
      }))
    )
);

export const getIsWorkflowPending = createSelector(
  getInitialState,
  ({ isWorkflowPending }) => isWorkflowPending
);

export const getDmsPrinterNo = createSelector(
  getInitialState,
  ({ manualCheckNoDetails }) => manualCheckNoDetails.dmsPrinterNo
);

export const getPrinterId = createSelector(
  getInitialState,
  ({ manualCheckNoDetails }) => manualCheckNoDetails.printerId
);

export const getArchiveOnly = createSelector(
  getInitialState,
  ({ manualCheckNoDetails }) => manualCheckNoDetails.archiveOnly
);

export const getManualCheckNo = createSelector(
  getInitialState,
  ({ manualCheckNoDetails }) =>
    manualCheckNoDetails.manualEntryCheckSequence.checkNumber
);

export const getIsSequenceForFutureChecks = createSelector(
  getInitialState,
  ({ manualCheckNoDetails }) =>
    manualCheckNoDetails.manualEntryCheckSequence.useSequenceForFutureChecks
);

export const getManualCheckDetails = createSelector(
  [
    getDmsPrinterNo,
    getPrinterId,
    getArchiveOnly,
    getManualCheckNo,
    getIsSequenceForFutureChecks
  ],
  (
    dmsPrinterNo,
    printerId,
    archiveOnly,
    manualCheckNumber,
    isSequenceForFutureChecks
  ) => ({
    dmsPrinterNo,
    printerId,
    archiveOnly,
    manualCheckNumber,
    isSequenceForFutureChecks
  })
);

export default printChecksSlice.reducer;
